import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Users, Mail, Phone, Calendar, ChevronDown, ChevronRight, FileText, ShieldCheck, Utensils, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const yesNoBadge = (val) => {
  if (val === true || val === 'Yes' || val === 'yes') {
    return <span className="text-red-700 font-semibold bg-red-100 px-1.5 py-0.5 rounded text-[11px]">YES</span>;
  }
  return <span className="text-emerald-700 font-semibold bg-emerald-100 px-1.5 py-0.5 rounded text-[11px]">No</span>;
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="border rounded-md p-3 bg-white">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />}<span>{title}</span>
    </div>
    <div className="text-sm text-gray-800 space-y-1">{children}</div>
  </div>
);

const KV = ({ k, v }) => (v === undefined || v === null || v === '' ? null : (
  <div className="flex items-start gap-2">
    <span className="text-gray-500 min-w-[140px]">{k}</span>
    <span className="text-gray-900 flex-1 break-words">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</span>
  </div>
));

const AttendeeCard = ({ label, attendee, medicalResponses, signatureName, signatureDate }) => {
  if (!attendee && !medicalResponses) return null;
  const a = attendee || {};
  const yesFlags = medicalResponses ? Object.entries(medicalResponses).filter(([, v]) => v === true || v === 'Yes' || v === 'yes') : [];
  return (
    <div className="border rounded-md p-3 bg-gray-50">
      <div className="text-xs uppercase tracking-wider text-[#0A2342] font-semibold mb-2">{label}</div>
      <div className="space-y-1 text-sm">
        <KV k="Name" v={a.name || a.full_name || a.fullName} />
        <KV k="Date of Birth" v={a.dob || a.date_of_birth || a.dateOfBirth || a.birthdate} />
        <KV k="Contact" v={a.phone || a.contact || a.contact_number} />
        <KV k="Email" v={a.email} />
        <KV k="Emergency Contact" v={a.emergency_contact || a.emergencyContact} />
        <KV k="Emergency Phone" v={a.emergency_phone || a.emergencyPhone} />
        <KV k="Certification" v={a.certification || a.qualification || a.certification_level || a.certificationLevel} />
        <KV k="Certifying Agency" v={a.agency || a.certifying_agency} />
        <KV k="Cert Number" v={a.cert_number || a.certification_number || a.certificationNumber} />
        <KV k="Total Dives" v={a.total_dives || a.totalDives || a.dive_count} />
        <KV k="Last Dive Date" v={a.last_dive_date || a.lastDiveDate || a.lastDive} />
        <KV k="Dietary Requirements" v={a.dietary || a.dietaryRequirements} />
        <KV k="Notes" v={a.notes || a.special_requirements || a.specialRequirements} />
      </div>
      {medicalResponses && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Medical Questionnaire</div>
          {yesFlags.length === 0 ? (
            <p className="text-xs text-emerald-700">No red-flag answers — cleared to dive.</p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-red-700 font-semibold">{yesFlags.length} "Yes" answers — medical review required.</p>
              {yesFlags.map(([q]) => (
                <div key={q} className="flex items-start gap-2 text-xs">
                  {yesNoBadge(true)}
                  <span className="text-gray-700 flex-1">{q}</span>
                </div>
              ))}
            </div>
          )}
          {signatureName && (
            <p className="text-[11px] text-gray-500 mt-2">Signed: <span className="text-gray-800 font-medium">{signatureName}</span>{signatureDate ? ` on ${signatureDate}` : ''}</p>
          )}
        </div>
      )}
    </div>
  );
};

const BookingCard = ({ b, index }) => {
  const [open, setOpen] = useState(index === 0);
  const booking = b.booking || {};
  const form = booking.formData || {};
  // TripBookingForm uses `primaryName / primaryEmail / primaryPhone /
  // primaryBirthdate / emergencyContact / emergencyPhone / certificationLevel
  // / dietaryRequirements / waiverSignatureName / medicalSignatureName`.
  const primary = {
    name: form.primaryName || form.name || b.customer_name,
    email: form.primaryEmail || form.email || b.customer_email,
    phone: form.primaryPhone || form.phone || b.customer_phone,
    dob: form.primaryBirthdate || form.dateOfBirth || form.dob,
    emergency_contact: form.emergencyContact,
    emergency_phone: form.emergencyPhone,
    certification: form.certificationLevel || form.certification,
    agency: form.certifyingAgency || form.agency,
    cert_number: form.certificationNumber || form.certNumber,
    total_dives: form.totalDives || form.diveCount,
    last_dive_date: form.lastDiveDate || form.lastDive,
    dietary: form.dietaryRequirements,
    notes: form.specialRequirements || form.notes,
  };
  // Account fallback for when the form values are missing.
  const accountLabel = (b.account_name && b.account_name !== primary.name)
    ? `${b.account_name}${b.account_email ? ` · ${b.account_email}` : ''}`
    : null;
  const additional = form.additionalAttendees || [];
  const medical = booking.medicalForms || {};
  // Row summary — prefer the primary name, then account name, then any
  // signature we captured; hard-fall to "Unknown" only if truly nothing.
  const rowName = primary.name || form.waiverSignatureName || form.medicalSignatureName || b.account_name || 'Unknown';
  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 text-left"
        data-testid={`booking-row-${index}`}
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#0A2342] truncate">{rowName}</div>
          <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{b.attendee_count} pax</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(b.order_created_at)}</span>
            {b.order_number && <span>Order #{b.order_number}</span>}
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${b.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{b.payment_status || 'unknown'}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-[#FF6B35]">R{Number(b.line_total || 0).toLocaleString()}</div>
        </div>
      </button>

      {open && (
        <div className="border-t p-4 space-y-4 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Section title="Contact" icon={Mail}>
              <KV k="Name" v={primary.name} />
              <KV k="Email" v={primary.email} />
              <KV k="Phone" v={primary.phone} />
              <KV k="Date of Birth" v={primary.dob} />
              {accountLabel && <KV k="Booked by (account)" v={accountLabel} />}
            </Section>
            <Section title="Booking summary" icon={FileText}>
              <KV k="Destination" v={booking.selectedDestination || b.line_name} />
              <KV k="Accommodation" v={booking.accommodation} />
              <KV k="Attendees" v={b.attendee_count} />
              <KV k="Trip days" v={booking.tripDays} />
              <KV k="Price / person" v={booking.pricePerPerson ? `R${Number(booking.pricePerPerson).toLocaleString()}` : null} />
              <KV k="Total" v={`R${Number(b.line_total || 0).toLocaleString()}`} />
            </Section>
            {(booking.gearHireCost > 0 || booking.gearSummary) && (
              <Section title="Gear hire" icon={Package}>
                <KV k="Summary" v={booking.gearSummary} />
                <KV k="Cost" v={booking.gearHireCost ? `R${Number(booking.gearHireCost).toLocaleString()}` : null} />
              </Section>
            )}
            {(booking.cateringOpted || booking.cateringCost > 0) && (
              <Section title="Catering" icon={Utensils}>
                <KV k="Opted in" v={booking.cateringOpted} />
                <KV k="Cost" v={booking.cateringCost ? `R${Number(booking.cateringCost).toLocaleString()}` : null} />
              </Section>
            )}
          </div>

          <AttendeeCard
            label="Primary attendee"
            attendee={primary}
            medicalResponses={medical.primary?.responses || form.medical}
            signatureName={medical.primary?.signature || form.medicalSignatureName}
            signatureDate={medical.primary?.signatureDate || form.medicalSignatureDate}
          />

          {(medical.additionalAttendees || additional || []).map((att, i) => (
            <AttendeeCard
              key={i}
              label={`Attendee ${i + 2}${additional[i]?.name ? ` — ${additional[i].name}` : ''}`}
              attendee={additional[i] || {}}
              medicalResponses={att.responses || additional[i]?.medical}
              signatureName={att.signature || additional[i]?.medicalSignatureName}
              signatureDate={att.signatureDate || additional[i]?.medicalSignatureDate}
            />
          ))}

          <Section title="Liability waiver" icon={ShieldCheck}>
            <KV k="Accepted" v={booking.waiverDetails?.accepted || form.waiverAccepted} />
            <KV k="Signed by" v={booking.waiverDetails?.signature || form.waiverSignatureName} />
            <KV k="Date" v={booking.waiverDetails?.signatureDate || form.waiverSignatureDate} />
          </Section>

          {(!booking || Object.keys(booking).length === 0) && (
            <div className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded p-2">
              This booking was placed before the booking-details feature was live, so only the order line-item is available. Contact the customer directly for medical / waiver info.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TripBookingsModal = ({ trip, token, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/admin/trips/${trip.id}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load bookings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [trip.id, token]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center overflow-y-auto p-2 md:p-6" data-testid="trip-bookings-modal">
      <div className="bg-white w-full max-w-5xl rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-heading font-semibold text-[#0A2342] text-lg">Bookings — {trip.destination}</h2>
            <p className="text-xs text-gray-500">
              {loading ? 'Loading…' : `${data?.total_bookings || 0} booking${(data?.total_bookings || 0) === 1 ? '' : 's'} · ${data?.total_attendees || 0} attendee${(data?.total_attendees || 0) === 1 ? '' : 's'}`}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-bookings-modal">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading bookings…
            </div>
          )}
          {!loading && (!data?.bookings || data.bookings.length === 0) && (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p>No bookings yet for this trip.</p>
            </div>
          )}
          {!loading && data?.bookings?.map((b, i) => (
            <BookingCard key={b.order_id || i} b={b} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TripBookingsModal;
